// The customer's GHL conversation, read AND written from inside the app.
//
// ── WHY THIS IS ITS OWN MODULE ───────────────────────────────────────────────
// Extracted verbatim from customer-detail.tsx so the RFQ page can mount it too.
// The desk spends ~90% of its time on /rfqs, and the only place to read or reply
// to the thread was a panel on the Customers page — so in practice the feature
// did not exist. Nothing here is new behaviour; the endpoint was already keyed
// by customer public id, which is exactly what the RFQ detail already holds.
//
// ── WHY IT STAYS LAZY ────────────────────────────────────────────────────────
// One load fans out to roughly 2-15 GHL calls (search conversations, then the
// messages, then a body fetch per email), and the server rate-limits to 30
// loads per tenant per minute. Auto-loading it on every RFQ open would spend
// that budget on RFQs nobody is corresponding about. So it stays behind the
// click it already had.
//
// ── WHAT IT CANNOT DO ────────────────────────────────────────────────────────
// There is no inbound webhook, so a customer REPLY is not pushed to us. It
// appears here when this panel is loaded or refreshed, and nothing in the app
// will announce it. Sending is real: POST .../messages goes out through GHL
// into the same thread, idempotency-keyed per send intent.

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import {
  MessageSquare,
  ExternalLink,
  RefreshCw,
  Send,
  Mail,
} from 'lucide-react';
import {
  api,
  type CustomerConversationResponse,
  type SendConversationMessageResponse,
} from '@/lib/api';

// Read-only GHL conversation thread for this customer. Lazy: nothing is fetched
// (no GHL calls) until the broker clicks "Load conversation history".
const CHANNEL_LABEL: Record<string, string> = {
  email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp', call: 'Call', social: 'Social', other: 'Msg',
};

export function CustomerConversation({ publicId }: { publicId: string }) {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['customer-conversation', publicId],
    queryFn: () =>
      api<CustomerConversationResponse>(`/api/conversations/customer/${publicId}`, {
        tokenGetter: () => getToken(),
      }),
    enabled: open,
    staleTime: 60_000,
  });

  if (!open) {
    return (
      <div className="flex items-center justify-between">
        <h3 className="eyebrow">Conversation</h3>
        <button onClick={() => setOpen(true)} className="btn-ghost text-xs">
          <MessageSquare className="w-3.5 h-3.5" /> Load conversation history
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="eyebrow">Conversation</h3>
        <div className="flex items-center gap-2">
          {data?.ghlDeepLink && (
            <a href={data.ghlDeepLink} target="_blank" rel="noopener noreferrer" className="btn-ghost text-xs">
              <ExternalLink className="w-3.5 h-3.5" /> Open in GHL
            </a>
          )}
          <button onClick={() => refetch()} disabled={isFetching} className="btn-ghost text-xs">
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {isLoading && <div className="text-xs text-foreground-muted">Loading conversation…</div>}
      {isError && (
        <div className="text-xs text-danger">
          {(error as { message?: string })?.message ?? 'Could not load conversation'}
        </div>
      )}

      {data && (
        <>
          {data.note && data.messages.length === 0 && (
            <div className="text-xs italic text-foreground-muted">{data.note}</div>
          )}
          {data.messages.length > 0 && (
            <>
              <ul className="space-y-2">
                {data.messages.map((m) => (
                  <li key={m.id} className={`flex ${m.direction === 'inbound' ? 'justify-start' : 'justify-end'}`}>
                    <div
                      className={`max-w-[85%] rounded-lg border px-2.5 py-1.5 text-xs ${
                        m.direction === 'inbound'
                          ? 'bg-surface-muted/50 border-border'
                          : 'bg-primary-muted border-primary-muted'
                      }`}
                    >
                      <div className="flex items-center gap-1.5 mb-0.5 text-[10px] text-foreground-muted flex-wrap">
                        <span className="font-semibold uppercase tracking-wide">{CHANNEL_LABEL[m.channel] ?? m.channel}</span>
                        <span>· {m.direction === 'inbound' ? 'received' : 'sent'}</span>
                        {m.dateAdded && <span>· {new Date(m.dateAdded).toLocaleString()}</span>}
                        {m.status && <span>· {m.status}</span>}
                      </div>
                      {m.subject && <div className="font-medium mb-0.5 break-words">{m.subject}</div>}
                      {m.body && (
                        <div className="whitespace-pre-wrap break-words text-foreground-muted max-h-44 overflow-auto">
                          {m.body}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {data.otherConversationCount > 0 && (
                <div className="mt-2 text-[11px] text-foreground-muted">
                  Showing the most recent thread.{' '}
                  {data.otherConversationCount} other conversation{data.otherConversationCount === 1 ? '' : 's'} —{' '}
                  {data.ghlDeepLink ? (
                    <a href={data.ghlDeepLink} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      view in GHL
                    </a>
                  ) : 'view in GHL'}.
                </div>
              )}
            </>
          )}

          {data.contactId ? (
            <ConversationComposer
              publicId={publicId}
              contactId={data.contactId}
              contactName={data.contactName}
              defaultChannel={
                data.messages.length > 0 && data.messages[data.messages.length - 1]?.channel === 'email'
                  ? 'Email'
                  : 'SMS'
              }
            />
          ) : (
            !data.note && (
              <div className="mt-3 text-xs italic text-foreground-muted">
                No GHL-linked contact to message yet.
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}

// Polished, mobile-first composer: pick SMS or Email, type, send. On success it
// optimistically appends the message to the thread (GHL reflects it on the next
// Refresh). The reply goes to the contact whose thread is shown.
function ConversationComposer({
  publicId,
  contactId,
  contactName,
  defaultChannel,
}: {
  publicId: string;
  contactId: string;
  contactName: string | null;
  defaultChannel: 'SMS' | 'Email';
}) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [channel, setChannel] = useState<'SMS' | 'Email'>(defaultChannel);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [err, setErr] = useState<string | null>(null);

  // Idempotency: a stable key per "send intent" so a double-click (or retry of
  // the same content) can't send the customer two messages. Minted lazily at
  // send time and reset whenever the content changes (so an edited message is a
  // new send) or after a success.
  const idemKeyRef = useRef<string | null>(null);
  const resetIdemKey = () => { idemKeyRef.current = null; };
  const idemKey = () => (idemKeyRef.current ??= crypto.randomUUID());

  const send = useMutation({
    mutationFn: () =>
      api<SendConversationMessageResponse>(`/api/conversations/customer/${publicId}/messages`, {
        method: 'POST',
        tokenGetter: () => getToken(),
        idempotencyKey: idemKey(),
        body: JSON.stringify({
          contactId,
          channel,
          message: message.trim(),
          subject: channel === 'Email' ? subject.trim() : undefined,
        }),
      }),
    onSuccess: () => {
      resetIdemKey();
      // Optimistically show the sent message; reconciles on next Refresh.
      qc.setQueryData<CustomerConversationResponse>(['customer-conversation', publicId], (old) =>
        old
          ? {
              ...old,
              messages: [
                ...old.messages,
                {
                  id: `local-${Date.now()}`,
                  direction: 'outbound',
                  channel: channel.toLowerCase(),
                  subject: channel === 'Email' ? subject.trim() || null : null,
                  body: message.trim(),
                  dateAdded: new Date().toISOString(),
                  status: 'sent',
                },
              ],
            }
          : old,
      );
      setMessage('');
      setSubject('');
      setErr(null);
    },
    onError: (e: { message?: string }) => setErr(e?.message ?? 'Could not send message'),
  });

  const needsSubject = channel === 'Email' && !subject.trim();
  const canSend = message.trim().length > 0 && !needsSubject && !send.isPending;

  return (
    <div className="mt-3 rounded-lg border border-border bg-surface p-2.5">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-[11px] text-foreground-muted">
          To <span className="font-medium text-foreground">{contactName ?? 'this contact'}</span>
        </div>
        <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
          {(['SMS', 'Email'] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => { setChannel(c); setErr(null); resetIdemKey(); }}
              className={`flex items-center gap-1 px-2.5 py-1 transition-colors ${
                channel === c
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-foreground-muted hover:bg-surface-muted'
              }`}
            >
              {c === 'SMS' ? <MessageSquare className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
              {c}
            </button>
          ))}
        </div>
      </div>

      {channel === 'Email' && (
        <input
          value={subject}
          onChange={(e) => { setSubject(e.target.value); resetIdemKey(); }}
          placeholder="Subject"
          maxLength={300}
          className="w-full mb-2 px-2 py-1.5 text-sm border border-border rounded bg-background"
        />
      )}

      <textarea
        value={message}
        onChange={(e) => { setMessage(e.target.value); resetIdemKey(); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canSend) {
            e.preventDefault();
            send.mutate();
          }
        }}
        rows={3}
        maxLength={5000}
        placeholder={channel === 'SMS' ? 'Type your text message…' : 'Type your email…'}
        className="w-full px-2 py-1.5 text-sm border border-border rounded bg-background resize-y min-h-[64px] max-h-72"
      />

      {err && <div className="mt-1.5 text-xs text-danger break-words">{err}</div>}

      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] text-foreground-muted">
          {channel === 'SMS'
            ? `${message.length} character${message.length === 1 ? '' : 's'}`
            : '⌘/Ctrl + Enter to send'}
        </div>
        <button
          onClick={() => send.mutate()}
          disabled={!canSend}
          className="btn-primary text-xs w-full sm:w-auto justify-center"
        >
          <Send className="w-3.5 h-3.5" />
          {send.isPending ? 'Sending…' : `Send ${channel}`}
        </button>
      </div>
    </div>
  );
}
